import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LocalStorageService } from '../../service/LocalStorageService';
import {
  appendDataToEndOfDoc,
  createNewDoc,
  getDocContent,
  getDocContentFilteredByParagraphs,
  getDocs,
  getPublicDocContent,
  prependDataToBeginningOfDoc,
  sendDataAsNewPageAfterSpecifiedPage,
  sendDataToBeginningOfDocAsNewPage,
  sendDataToEndOfDocAsNewPage,
} from './helpers';
import { useGoogleSheet } from '../GoogleSheetsIntegration/GoogleSheetsContext';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { AuthService } from '../../service/AuthService';
import { useAuthStore } from '../../store/storeAuth';

export type Doc = {
  id: string;
  name: string;
};

export type Docs = Doc[];

export enum PushDataOption {
  BEGINNING_OF_DOC = 'Beginning of document',
  END_OF_DOC = 'End of document',
  BEGINNING_OF_DOC_AS_A_NEW_PAGE = 'Start of document as a new page',
  END_OF_DOC_AS_A_NEW_PAGE = 'End of document as a new page',
  NEW_PAGE_AFTER_SPECIFIED_PAGE = 'After a specific page',
}

const GoogleDocsContext = createContext<{
  docs: Docs;
  selectedDoc: undefined | Doc;
  currentDocContent: string;
  getDocsHandler: () => Promise<void>;
  updateSelectedDoc: (selectedDocId: string) => void;
  getFullContent: () => Promise<any>;
  getPublicContent: () => Promise<any>;
  getFilteredDocContentByParagraphsHandler: (
    paragraphStart: number,
    paragraphEnd: number,
  ) => Promise<string>;
  refreshDocsHandler: () => Promise<void>;
  pushDataHandler: (
    pushDataOption: PushDataOption,
    content: string,
    page?: number,
  ) => Promise<void>;
  updateSelectedPublicDoc: (publicDocId: string) => Promise<void>;
  createAndPushDataToNewDoc: (
    title: string,
    content: string,
    pushDataOption: PushDataOption,
    page?: number,
  ) => Promise<void>;

  title: string;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
}>({
  docs: [],
  selectedDoc: undefined,
  currentDocContent: '',
  async getDocsHandler() {},
  updateSelectedDoc(selectedDocId) {},
  getFullContent: async () => '',
  getFilteredDocContentByParagraphsHandler: async (
    paragraphStart: number,
    paragraphEnd: number,
  ) => '',
  async refreshDocsHandler() {},
  async pushDataHandler(pushDataOption, content) {},
  async updateSelectedPublicDoc(publicDocId) {},
  async getPublicContent() {},
  async createAndPushDataToNewDoc(title, content) {},
  title: '',
  setTitle() {},
});

const GoogleDocsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { googleAccessToken, refreshToken, setGoogleToken } = useAuthStore(
    state => state,
  );

  const { isConnected, setIsRefreshing, disconnectHandler, setIsConnected } =
    useGoogleSheet();
  const [googleSheetsAccessToken, setGoogleSheetsAccessToken] = useState<
    undefined | string
  >();

  const [docs, setDocs] = useState<Docs>([]);
  const [selectedDoc, setSelectedDoc] = useState<undefined | Doc>(undefined);
  const [currentDocContent, setCurrentDocContent] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (googleAccessToken) {
      setGoogleSheetsAccessToken(googleAccessToken);
      setIsConnected(true);
    } else {
      setGoogleSheetsAccessToken(undefined);
      setIsConnected(false);
    }
  }, [
    googleAccessToken,
    refreshToken,
    setGoogleSheetsAccessToken,
    setIsConnected,
  ]);

  const disconnectHandlerGoogle = async () => {
    if (!refreshToken) {
      setNotification({
        type: NotificationType.Error,
        message: 'Session expired. Please login again.',
      });
      return;
    }

    await refreshAuthToken(refreshToken);
  };

  const refreshAuthToken = async (refreshToken: string) => {
    try {
      const newTokens = await AuthService.googleRefreshToken({
        refresh_token: refreshToken,
      });

      LocalStorageService.setItem(
        'googleSheetsAccessToken',
        newTokens.data.access_token,
      );
      LocalStorageService.setItem(
        'googleRefreshToken',
        newTokens.data.refresh_token,
      );

      setGoogleSheetsAccessToken(newTokens.data.access_token);
      setIsConnected(true);

      const docs = await getDocs(newTokens.data.access_token, title);
      setDocs(docs);
    } catch (refreshError) {
      // setNotification({
      //   type: NotificationType.Error,
      //   message: 'Failed to refresh session, please login again.',
      // });
      // localStorage.removeItem('googleSheetsAccessToken');
      // localStorage.removeItem('googleRefreshToken');
      // setGoogleToken(null);
    }
  };

  const getDocsHandler = useCallback(async () => {
    if (!googleSheetsAccessToken) {
      return disconnectHandlerGoogle();
    }

    try {
      const docs = await getDocs(googleSheetsAccessToken, title);
      setDocs(docs);
    } catch (error) {
      let anyError = error as any;
      if (anyError.response.status === 401) {
        if (!refreshToken) {
          setNotification({
            type: NotificationType.Error,
            message: 'Session expired. Please login again.',
          });
          return;
        }

        await refreshAuthToken(refreshToken);
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    }
  }, [googleSheetsAccessToken, title]);

  const updateSelectedDoc = useCallback(
    (selectedDocId: string) => {
      const selectedDoc = docs.find(({ id }) => id === selectedDocId);

      if (selectedDoc) {
        setSelectedDoc(selectedDoc);
      } else {
        setSelectedDoc(undefined);
      }
    },
    [docs],
  );

  const updateSelectedPublicDoc = async (publicDocId: string) => {
    setSelectedDoc({ id: publicDocId, name: '' });
  };

  const refreshDocsHandler = async () => {
    setIsRefreshing(true);
    try {
      await getDocsHandler();
    } catch (error) {
      let anyError = error as any;
      if (anyError.response.status === 401) {
        disconnectHandlerGoogle();
        if (!refreshToken) {
          setNotification({
            type: NotificationType.Error,
            message: 'Session expired. Please login again.',
          });
          return;
        }

        await refreshAuthToken(refreshToken);
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    }
    setIsRefreshing(false);
  };

  const getFullContent = async () => {
    if (!googleSheetsAccessToken || !selectedDoc)
      throw 'You have not selected a doc!';

    try {
      const fullContent = await getDocContent(
        googleSheetsAccessToken,
        selectedDoc.id,
      );

      return fullContent;
    } catch (error) {
      let anyError = error as any;
      if (anyError.code === 401) {
        disconnectHandlerGoogle();
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
      throw error;
    }
  };

  useEffect(() => {
    const updateCurrentDocContent = async () => {
      try {
        const currentDocContent = await getFullContent();
        setCurrentDocContent(currentDocContent);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    };
    if (selectedDoc && googleSheetsAccessToken) {
      updateCurrentDocContent();
    }
  }, [googleSheetsAccessToken, selectedDoc]);

  const getPublicContent = async () => {
    if (!selectedDoc) throw 'You have not selected a doc!';

    try {
      const fullContent = await getPublicDocContent(selectedDoc.id);

      return fullContent;
    } catch (error) {
      let anyError = error as any;
      if (anyError.code === 401) {
        disconnectHandlerGoogle();
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
      throw error;
    }
  };

  const getFilteredDocContentByParagraphsHandler = async (
    paragraphStart: number,
    paragraphEnd: number,
  ) => {
    if (!googleSheetsAccessToken || !selectedDoc)
      throw 'You have not selected a doc!';

    try {
      const content = await getDocContentFilteredByParagraphs(
        googleSheetsAccessToken,
        selectedDoc.id,
        paragraphStart,
        paragraphEnd,
      );

      return content;
    } catch (error) {
      let anyError = error as any;
      if (anyError.code === 401) {
        disconnectHandlerGoogle();
      }
      throw error;
    }
  };

  const createAndPushDataToNewDoc = async (
    title: string,
    content: string,
    pushDataOption: PushDataOption,
    page?: number,
  ) => {
    if (!googleSheetsAccessToken) throw 'You are not connected to Google!';

    try {
      const newDoc = await createNewDoc(googleSheetsAccessToken, title);

      if (pushDataOption === PushDataOption.BEGINNING_OF_DOC) {
        await prependDataToBeginningOfDoc(
          googleSheetsAccessToken,
          newDoc.documentId,
          content + '\n',
        );
      } else if (pushDataOption === PushDataOption.END_OF_DOC) {
        await appendDataToEndOfDoc(
          googleSheetsAccessToken,
          newDoc.documentId,
          '\n' + content,
        );
      } else if (pushDataOption === PushDataOption.END_OF_DOC_AS_A_NEW_PAGE) {
        await sendDataToEndOfDocAsNewPage(
          googleSheetsAccessToken,
          newDoc.documentId,
          content,
        );
      } else if (
        pushDataOption === PushDataOption.BEGINNING_OF_DOC_AS_A_NEW_PAGE
      ) {
        await sendDataToBeginningOfDocAsNewPage(
          googleSheetsAccessToken,
          newDoc.documentId,
          content,
        );
      } else if (
        pushDataOption === PushDataOption.NEW_PAGE_AFTER_SPECIFIED_PAGE
      ) {
        if (!page || isNaN(page) || +page < 0) throw 'Page must be specified!';

        await sendDataAsNewPageAfterSpecifiedPage(
          googleSheetsAccessToken,
          newDoc.documentId,
          content,
          page,
        );
      }

      await getDocsHandler();

      setSelectedDoc({ id: newDoc.documentId, name: title });
    } catch (error) {
      let anyError = error as any;
      if (anyError.code === 401) {
        disconnectHandlerGoogle();
      }

      throw error;
    }
  };

  const pushDataHandler = async (
    pushDataOption: PushDataOption,
    content: string,
    page?: number,
  ) => {
    if (!googleSheetsAccessToken) throw 'You are not connected to Google!';
    if (!selectedDoc) throw 'You have not selected a document!';

    try {
      if (pushDataOption === PushDataOption.BEGINNING_OF_DOC) {
        await prependDataToBeginningOfDoc(
          googleSheetsAccessToken,
          selectedDoc.id,
          content + '\n',
        );
      } else if (pushDataOption === PushDataOption.END_OF_DOC) {
        await appendDataToEndOfDoc(
          googleSheetsAccessToken,
          selectedDoc.id,
          '\n' + content,
        );
      } else if (pushDataOption === PushDataOption.END_OF_DOC_AS_A_NEW_PAGE) {
        await sendDataToEndOfDocAsNewPage(
          googleSheetsAccessToken,
          selectedDoc.id,
          content,
        );
      } else if (
        pushDataOption === PushDataOption.BEGINNING_OF_DOC_AS_A_NEW_PAGE
      ) {
        await sendDataToBeginningOfDocAsNewPage(
          googleSheetsAccessToken,
          selectedDoc.id,
          content,
        );
      } else if (
        pushDataOption === PushDataOption.NEW_PAGE_AFTER_SPECIFIED_PAGE
      ) {
        if (!page || isNaN(page) || +page < 0) throw 'Page must be specified!';

        await sendDataAsNewPageAfterSpecifiedPage(
          googleSheetsAccessToken,
          selectedDoc.id,
          content,
          page,
        );
      }
    } catch (error) {
      let anyError = error as any;
      if (anyError.code === 401) {
        disconnectHandlerGoogle();
      }

      throw error;
    }
  };

  useEffect(() => {
    if (isConnected) getDocsHandler();
  }, [isConnected, getDocsHandler]);

  return (
    <GoogleDocsContext.Provider
      value={{
        docs,
        selectedDoc,
        currentDocContent,
        getDocsHandler,
        updateSelectedDoc,
        getFullContent,
        getFilteredDocContentByParagraphsHandler,
        refreshDocsHandler,
        pushDataHandler,
        updateSelectedPublicDoc,
        getPublicContent,
        setTitle,
        title,
        createAndPushDataToNewDoc,
      }}
    >
      {children}
    </GoogleDocsContext.Provider>
  );
};

const useGoogleDocs = function () {
  const context = useContext(GoogleDocsContext);

  if (!context)
    throw new Error('GoogleDocsContext is used outside of GoogleDocsProvider');

  return context;
};

export { useGoogleDocs, GoogleDocsProvider };
